/**
 * Application color variant type.
 *
 * Maps to Tailwind color classes via the colorVariantClasses lookup below.
 * Each variant has bg (chip/badge background), text (foreground), and
 * border classes for both light and dark modes.
 */
export type AppColorVariant =
  | "primary"
  | "neutral"
  | "danger"
  | "success"
  | "warning"
  | "pink"
  | "lime"
  | "yellow"
  | "lavender";

export interface ColorVariantClasses {
  bg: string;
  text: string;
  border: string;
}

/**
 * Tailwind class lookup for each color variant.
 * Used by Chip, Badge, and status indicator components.
 *
 * NOTE: These classes must be complete literals (not dynamically constructed)
 * so that Tailwind can detect them at build time.
 */
export const colorVariantClasses: Record<AppColorVariant, ColorVariantClasses> = {
  primary: {
    bg: "bg-primary/15 dark:bg-primary/25",
    text: "text-primary dark:text-primary",
    border: "border-primary/30",
  },
  neutral: {
    bg: "bg-muted",
    text: "text-muted-foreground",
    border: "border-border",
  },
  danger: {
    bg: "bg-destructive/15 dark:bg-destructive/25",
    text: "text-destructive dark:text-red-400",
    border: "border-destructive/30",
  },
  success: {
    bg: "bg-emerald-100 dark:bg-emerald-900/40",
    text: "text-emerald-800 dark:text-emerald-300",
    border: "border-emerald-300 dark:border-emerald-700",
  },
  warning: {
    bg: "bg-amber-100 dark:bg-amber-900/40",
    text: "text-amber-800 dark:text-amber-300",
    border: "border-amber-300 dark:border-amber-700",
  },
  pink: {
    bg: "bg-pink-200 dark:bg-pink-800/40",
    text: "text-pink-800 dark:text-pink-300",
    border: "border-pink-300 dark:border-pink-700",
  },
  lime: {
    bg: "bg-lime-200 dark:bg-lime-800/40",
    text: "text-lime-800 dark:text-lime-300",
    border: "border-lime-300 dark:border-lime-700",
  },
  yellow: {
    bg: "bg-yellow-200 dark:bg-yellow-800/40",
    text: "text-yellow-800 dark:text-yellow-300",
    border: "border-yellow-300 dark:border-yellow-700",
  },
  lavender: {
    bg: "bg-lavender-200 dark:bg-lavender-200/40",
    text: "text-lavender-800 dark:text-lavender-700",
    border: "border-lavender-300 dark:border-lavender-500",
  },
};
